import { DestroyRef, Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, EMPTY, Subscription } from 'rxjs';
import { filter, finalize, switchMap, take } from 'rxjs/operators';
import { DashboardProject } from '../../dashboard-task/models/dashboard-task.model';
import { isActiveMasterMergeFeature } from 'src/app/config/master-merge-feature.flags';
import { P } from 'src/app/config/portal-permissions.constants';
import { PermissionService } from 'src/app/core/permissions/permission.service';
import { DashboardTaskService } from '../../dashboard-task/services/dashboard-task.service';
import { ProjectWorkflowTask } from '../models/project-management.model';
import { ProjectManagementService } from './project-management.service';

const SESSION_ACTIVE_PROJECT_ID = 'activeWorkflowProjectId';
const SESSION_ACTIVE_PROJECT_IDS = 'activeWorkflowProjectIds';
const SESSION_ACTIVE_COMPANY_ID = 'activeWorkflowCompanyId';
const SESSION_PROJECT_STATUS_BY_ID = 'activeWorkflowProjectStatusById';

/**
 * Sekmeler arası aktif proje senkronu (sessionStorage sekme bazlı olduğu için).
 * Yalnızca proje/şirket id — görev içeriği session’da kalır.
 */
const LS_SYNC_PROJECT_ID = 'activeWorkflowProjectId_sync';
const LS_SYNC_PROJECT_IDS = 'activeWorkflowProjectIds_sync';
const LS_SYNC_COMPANY_ID = 'activeWorkflowCompanyId_sync';

/** Session key shared with PM / calculation (task context from click or auto-match). */
export const PROJECT_WORKFLOW_TASK_CONTEXT_KEY = 'projectWorkflowTaskContext';

function normalizeStatus(s: string | undefined | null): string {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

export interface RouteTaskHint {
  categoryCode: string | null;
  methodCode: string | null;
}

export interface WorkflowTaskContext {
  projectId: string;
  taskId: string;
  title?: string | null;
  status?: string | null;
  methodCode?: string | null;
  categoryId?: string | null;
  locationId?: string | null;
  categoryCode?: string | null;
  sourceQuestionId?: string | null;
  actionType?: string | null;
  actionPayloadJson?: string | null;
  url?: string | null;
  /** calculation = hesaplama sayfası toggle; url = dahili route üzerinden açılan görev */
  contextKind?: 'calculation' | 'url';
  /** Dahili path eşlemesi (normalizeWorkflowPath) */
  targetUrl?: string | null;
}

/** Dahili SPA path karşılaştırması (query/hash yok, küçük harf, tek slash). */
export function normalizeWorkflowPath(raw: string): string {
  let u = raw.split('?')[0].split('#')[0].trim();
  if (!u.startsWith('/')) {
    u = `/${u}`;
  }
  u = u.replace(/\/+/g, '/');
  if (u.length > 1 && u.endsWith('/')) {
    u = u.slice(0, -1);
  }
  return u.toLowerCase();
}

export function workflowPathsMatch(routerUrl: string, targetPath: string): boolean {
  const cur = normalizeWorkflowPath(routerUrl);
  const target = normalizeWorkflowPath(targetPath);
  if (cur === target) {
    return true;
  }
  return cur.startsWith(`${target}/`);
}

function pickPayloadString(
  obj: Record<string, unknown> | undefined,
  keys: string[],
): string | null {
  if (!obj) {
    return null;
  }
  for (const k of keys) {
    const v = obj[k];
    if (v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return null;
}

export function extractCodesFromTask(task: ProjectWorkflowTask): {
  categoryCode: string | null;
  methodCode: string | null;
} {
  let categoryCode = task.categoryCode?.trim() || null;
  let methodCode = task.methodCode?.trim() || null;
  if (!task.actionPayloadJson) {
    return { categoryCode, methodCode };
  }
  try {
    const j = JSON.parse(task.actionPayloadJson) as Record<string, unknown>;
    const av = j['answerValueJson'] as Record<string, unknown> | undefined;
    categoryCode =
      pickPayloadString(j, [
        'categoryCode',
        'CategoryCode',
        'category',
        'Category',
      ]) ||
      pickPayloadString(av, [
        'categoryCode',
        'CategoryCode',
        'category',
        'Category',
      ]) ||
      categoryCode;
    methodCode =
      pickPayloadString(j, ['methodCode', 'MethodCode', 'method', 'Method']) ||
      pickPayloadString(av, ['methodCode', 'MethodCode', 'method', 'Method']) ||
      methodCode;
  } catch {
    /* ignore */
  }
  return {
    categoryCode: categoryCode?.trim() || null,
    methodCode: methodCode?.trim() || null,
  };
}

/** Router bazen categoryName paramını geç verir; URL’den son segment okunur */
function extractCategoryNameFromMethodsUrl(url: string): string | null {
  const raw = url.split('?')[0].split('#')[0];
  const m = raw.match(/\/methods\/[^/]+\/[^/]+\/[^/]+\/([^/?#]+)/i);
  if (!m?.[1]) {
    return null;
  }
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

function collectRouteParams(route: ActivatedRoute): Record<string, string> {
  const out: Record<string, string> = {};
  const chain: ActivatedRoute[] = [];
  let r: ActivatedRoute | null = route;
  while (r) {
    chain.unshift(r);
    r = r.parent ?? null;
  }
  for (const x of chain) {
    const pm = x.snapshot.paramMap;
    for (const k of pm.keys) {
      const v = pm.get(k);
      if (v != null && v !== '') {
        out[k] = v;
      }
    }
  }
  return out;
}

function resolveRouteHint(
  url: string,
  params: Record<string, string>,
): RouteTaskHint {
  const path = url.split('?')[0].toLowerCase();

  if (params['categoryName']) {
    return { categoryCode: params['categoryName'], methodCode: null };
  }
  const fromMethodsPath = extractCategoryNameFromMethodsUrl(url);
  if (fromMethodsPath) {
    return { categoryCode: fromMethodsPath, methodCode: null };
  }

  if (path.includes('/aviation-mobile-combustion/')) {
    return {
      categoryCode: 'MobileCombustion',
      methodCode: 'MOBILE_CONSUMPTION_CIVIL_AVIATION',
    };
  }

  if (path.includes('/purchased-electricity-combustion/')) {
    const from = (params['from'] || '').toLowerCase();
    if (from === 'other') {
      return {
        categoryCode: 'OtherEnergyPurchaseElectricity',
        methodCode: 'ELECTRICITY_USAGE',
      };
    }
    return {
      categoryCode: 'PurchasedElectricity',
      methodCode: 'ELECTRICITY_USAGE',
    };
  }

  const rules: Array<{
    when: boolean;
    hint: RouteTaskHint;
  }> = [
    {
      when: path.includes('/stationary-combustion/'),
      hint: { categoryCode: 'StationaryCombustion', methodCode: null },
    },
    {
      when: path.includes('/mobile-combustion/'),
      hint: { categoryCode: 'MobileCombustion', methodCode: null },
    },
    {
      when: path.includes('/fugitive-combustion/'),
      hint: { categoryCode: 'FugitiveCombustion', methodCode: null },
    },
    {
      when: path.includes('/wttmanual/'),
      hint: { categoryCode: 'WTTManual', methodCode: null },
    },
    { when: path.includes('/wtt/'), hint: { categoryCode: 'WTT', methodCode: null } },
    {
      when: path.includes('/investments/'),
      hint: { categoryCode: 'Investments', methodCode: null },
    },
    { when: path.includes('/waste/'), hint: { categoryCode: 'Waste', methodCode: null } },
    {
      when: path.includes('/expenses/'),
      hint: { categoryCode: 'Expenses', methodCode: null },
    },
    {
      when: path.includes('/supplier-emission/'),
      hint: { categoryCode: 'SupplierEmission', methodCode: null },
    },
    {
      when: path.includes('/manual-emission/'),
      hint: { categoryCode: 'ManualEmission', methodCode: null },
    },
    {
      when: path.includes('/working-home-emission/'),
      hint: { categoryCode: 'WorkingHomeEmission', methodCode: null },
    },
    {
      when: path.includes('/hotel-accommodation-emission/'),
      hint: { categoryCode: 'HotelAccommodationEmission', methodCode: null },
    },
    {
      when: path.includes('/process-emission/'),
      hint: { categoryCode: 'ProcessEmissions', methodCode: null },
    },
    {
      when: path.includes('/business-travel-airway-transport/'),
      hint: {
        categoryCode: 'BusinessTravelAirwayTransport',
        methodCode: null,
      },
    },
    {
      when: path.includes('/layer-airway-transport/'),
      hint: { categoryCode: 'LayerAirwayTransport', methodCode: null },
    },
    {
      when: path.includes('/logistic-emission/'),
      hint: { categoryCode: 'LogisticEmission', methodCode: null },
    },
    {
      when: path.includes('/pcaf/'),
      hint: { categoryCode: 'FranchiesPcafCombustion', methodCode: null },
    },
    {
      when: path.includes('/wastewater-emission/'),
      hint: { categoryCode: 'FugitiveWastewaterEmission', methodCode: null },
    },
    {
      when: path.includes('/cooling-and-fire-emission/'),
      hint: {
        categoryCode: 'FugitiveCoolingAndFireEmission',
        methodCode: null,
      },
    },
    {
      when: path.includes('/distance-based-calculation/'),
      hint: { categoryCode: 'EmployeeTransportationServices', methodCode: null },
    },
    {
      when: path.includes('/travel-based-calculation/'),
      hint: { categoryCode: 'EmployeeTransportationServices', methodCode: null },
    },
    {
      when:
        path.includes('/calculation/services/') &&
        !path.includes('/services/mobile-combustion') &&
        !path.includes('/services/distance-based') &&
        !path.includes('/services/travel-based'),
      hint: { categoryCode: 'EmployeeTransportationServices', methodCode: null },
    },
  ];

  for (const r of rules) {
    if (r.when) {
      return r.hint;
    }
  }

  return { categoryCode: null, methodCode: null };
}

function isTaskCompleted(task: ProjectWorkflowTask): boolean {
  return String(task.status ?? '').toLowerCase() === 'completed';
}

function scoreTaskMatch(
  task: ProjectWorkflowTask,
  hint: RouteTaskHint,
  options?: { routingLayer?: boolean },
): number {
  const { categoryCode: tCat, methodCode: tMeth } = extractCodesFromTask(task);
  if (!hint.categoryCode || !tCat) {
    return -1;
  }
  if (tCat.toLowerCase() !== hint.categoryCode.toLowerCase()) {
    return -1;
  }
  const routingLayer = options?.routingLayer ?? false;
  /** Routing katmanı (/methods/...): yalnızca kategori düzeyi (methodCode yok) görevler */
  if (routingLayer && tMeth) {
    return -1;
  }
  /** Tamamlanmış görevler de eşleşsin (yalnızca açık görev yoksa seçilir); açık görevler her zaman önde */
  const completed = isTaskCompleted(task);
  let score = completed ? 2 : 20;
  if (hint.methodCode && tMeth) {
    if (tMeth.toLowerCase() === hint.methodCode.toLowerCase()) {
      score += 8;
    } else {
      return -1;
    }
  } else if (hint.methodCode && !tMeth) {
    score += 2;
  } else if (!hint.methodCode && tMeth) {
    /** Hesaplama detay rotasında method görevi, saf kategori görevinden önce gelir */
    score += 8;
  } else {
    score += 3;
  }
  score += 1 / (1 + (task.questionOrder ?? 0));
  return score;
}

@Injectable({ providedIn: 'root' })
export class ActiveProjectWorkflowService {
  readonly activeProjectIds$ = new BehaviorSubject<string[]>([]);
  readonly activeProjectId$ = new BehaviorSubject<string | null>(null);
  readonly tasks$ = new BehaviorSubject<ProjectWorkflowTask[]>([]);

  private readonly tasksByProjectId = new Map<string, ProjectWorkflowTask[]>();
  private readonly tasksSubs = new Map<string, Subscription>();
  private readonly loadTasksProjectIds = new Set<string>();
  /** Üst üste ensureHydrated → tek GetProjects */
  private getProjectsInFlight = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private dashboardTaskService: DashboardTaskService,
    private projectManagementService: ProjectManagementService,
    private permissionService: PermissionService,
  ) {
    if (typeof window !== 'undefined') {
      const onStorage = (e: StorageEvent): void => {
        if (e.key !== LS_SYNC_PROJECT_ID && e.key !== LS_SYNC_PROJECT_IDS) {
          return;
        }
        if (e.newValue === null) {
          this.clearAll();
          return;
        }
        const comp = localStorage.getItem(LS_SYNC_COMPANY_ID);
        const ids = this.readSyncedProjectIds();
        if (!ids.length) {
          return;
        }
        if (JSON.stringify(ids) !== JSON.stringify(this.activeProjectIds$.value)) {
          this.applyProjectsFromOtherTab(ids, comp);
        }
      };
      const onVisibility = (): void => {
        if (document.visibilityState !== 'visible') {
          return;
        }
        const ids = this.readSyncedProjectIds();
        if (!ids.length) {
          return;
        }
        const sess = this.readPersistedProjectIds();
        if (
          JSON.stringify(ids) !== JSON.stringify(this.activeProjectIds$.value) ||
          JSON.stringify(ids) !== JSON.stringify(sess)
        ) {
          const comp = localStorage.getItem(LS_SYNC_COMPANY_ID);
          this.applyProjectsFromOtherTab(ids, comp);
        }
      };
      window.addEventListener('storage', onStorage);
      document.addEventListener('visibilitychange', onVisibility);
      this.destroyRef.onDestroy(() => {
        window.removeEventListener('storage', onStorage);
        document.removeEventListener('visibilitychange', onVisibility);
      });
    }
  }

  private writeCrossTabSync(projectIds: string[], companyId: string | null): void {
    try {
      const normalized = [...new Set((projectIds || []).filter(Boolean))];
      localStorage.setItem(LS_SYNC_PROJECT_IDS, JSON.stringify(normalized));
      localStorage.setItem(LS_SYNC_PROJECT_ID, normalized[0] ?? '');
      if (companyId) {
        localStorage.setItem(LS_SYNC_COMPANY_ID, companyId);
      } else {
        localStorage.removeItem(LS_SYNC_COMPANY_ID);
      }
    } catch {
      /* private mode / quota */
    }
  }

  /** Başka sekmede veya ilk yüklemede localStorage’daki aktif projeleri bu sekmeye uygular */
  private applyProjectsFromOtherTab(
    projectIds: string[],
    companyId: string | null,
  ): void {
    const cid = this.getCurrentCompanyId();
    if (
      companyId &&
      cid &&
      String(companyId) !== String(cid)
    ) {
      return;
    }
    const ids = [...new Set((projectIds || []).filter(Boolean))];
    if (!ids.length) {
      return;
    }
    sessionStorage.removeItem(PROJECT_WORKFLOW_TASK_CONTEXT_KEY);
    const persistCompany = companyId || cid || null;
    this.setActiveProjects(ids, persistCompany, true);
  }

  private reconcileCrossTabOnHydrate(): void {
    const ids = this.readSyncedProjectIds();
    if (!ids.length) {
      return;
    }
    const sess = this.readPersistedProjectIds();
    if (
      JSON.stringify(ids) === JSON.stringify(this.activeProjectIds$.value) &&
      JSON.stringify(ids) === JSON.stringify(sess) &&
      this.tasks$.value.length > 0
    ) {
      return;
    }
    const comp = localStorage.getItem(LS_SYNC_COMPANY_ID);
    this.applyProjectsFromOtherTab(ids, comp);
  }

  /** Remove persisted workflow keys (logout / company switch). Session + sekme senkronu. */
  static clearPersistedKeys(): void {
    sessionStorage.removeItem(SESSION_ACTIVE_PROJECT_ID);
    sessionStorage.removeItem(SESSION_ACTIVE_PROJECT_IDS);
    sessionStorage.removeItem(SESSION_ACTIVE_COMPANY_ID);
    sessionStorage.removeItem(PROJECT_WORKFLOW_TASK_CONTEXT_KEY);
    try {
      localStorage.removeItem(LS_SYNC_PROJECT_ID);
      localStorage.removeItem(LS_SYNC_PROJECT_IDS);
      localStorage.removeItem(LS_SYNC_COMPANY_ID);
    } catch {
      /* ignore */
    }
  }

  getCurrentCompanyId(): string | null {
    const admin = sessionStorage.getItem('applicationAdminSelectedCompanyId');
    if (admin) {
      return admin;
    }
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        return null;
      }
      const u = JSON.parse(raw) as {
        companyId?: string;
        branchId?: string;
      };
      return u.companyId || u.branchId || null;
    } catch {
      return null;
    }
  }

  clearAll(): void {
    this.tasksSubs.forEach((sub) => sub.unsubscribe());
    this.tasksSubs.clear();
    this.loadTasksProjectIds.clear();
    this.tasksByProjectId.clear();
    this.getProjectsInFlight = false;
    this.activeProjectIds$.next([]);
    this.activeProjectId$.next(null);
    this.tasks$.next([]);
    ActiveProjectWorkflowService.clearPersistedKeys();
    sessionStorage.removeItem(SESSION_PROJECT_STATUS_BY_ID);
  }

  /**
   * GetProjects listesinden workflow için proje seçer.
   * API çoğu zaman `InProgress` döner; yalnızca `active` aramak görevlerin hiç yüklenmemesine yol açıyordu.
   */
  private pickProjectsForWorkflow(
    projects: DashboardProject[] | null | undefined,
  ): DashboardProject[] {
    if (!projects?.length) {
      return [];
    }
    const st = (p: DashboardProject) => normalizeStatus(p.status);
    const active = projects.filter((p) =>
      ['active', 'inprogress', 'running'].includes(st(p)),
    );
    if (active.length) {
      return active;
    }
    const open = projects.filter((p) =>
      !['completed', 'cancelled', 'archived'].includes(st(p)),
    );
    return open;
  }

  /**
   * Dashboard `getProjects` cevabıyla aktif projeyi ve görevleri günceller.
   * Aktif yoksa veya şirket uyuşmazsa state + session temizlenir.
   */
  applyProjectsFromDashboard(projects: DashboardProject[]): void {
    const companyId = this.getCurrentCompanyId();
    this.persistProjectStatuses(projects);
    const selected = this.pickProjectsForWorkflow(projects).filter(
      (p) => !!p.projectId,
    );
    if (!selected.length) {
      this.clearAll();
      return;
    }
    const hasCompanyMismatch = selected.some(
      (p) =>
        companyId &&
        p.companyId &&
        String(p.companyId) !== String(companyId),
    );
    if (hasCompanyMismatch) {
      this.clearAll();
      return;
    }
    const persistCompany = companyId || selected[0]?.companyId || null;
    this.setActiveProjects(
      selected.map((p) => p.projectId),
      persistCompany,
      true,
    );
  }

  /**
   * PM sayfasındaki GetProjectTasks sonucunu workflow ile paylaşır (tasks$ + session project id).
   * Böylece yalnızca PM’den görev tıklanınca session yazılsa bile aynı sekmede tasks$ dolu kalır.
   */
  seedTasksFromProjectManagementLoad(
    projectId: string,
    tasks: ProjectWorkflowTask[],
  ): void {
    const companyId = this.getCurrentCompanyId();
    const ids = [...new Set([...(this.activeProjectIds$.value || []), projectId])];
    this.setActiveProjects(ids, companyId, false);
    this.tasksByProjectId.set(projectId, tasks ?? []);
    this.publishTasks();
    this.syncWorkflowTaskSessionFromTaskList(this.tasks$.value);
  }

  /**
   * Önce session’daki aktif proje id; yoksa GetProjects ile aktif proje, ardından GetProjectTasks.
   * Yalnızca sessionStorage kullanılır (sekme kapanınca silinir).
   */
  ensureHydrated(): void {
    if (!isActiveMasterMergeFeature('projectManagement')) {
      return;
    }

    this.reconcileCrossTabOnHydrate();
    const companyId = this.getCurrentCompanyId();
    const storedCompany = sessionStorage.getItem(SESSION_ACTIVE_COMPANY_ID);
    const storedProjects = this.readPersistedProjectIds();

    if (
      storedCompany &&
      companyId &&
      String(storedCompany) !== String(companyId)
    ) {
      this.clearAll();
      /* return yok: aşağıda getProjects ile yeniden dolsun */
    }

    if (this.activeProjectIds$.value.length > 0 && this.tasks$.value.length > 0) {
      return;
    }

    if (storedProjects.length) {
      if (!companyId || !storedCompany || storedCompany === companyId) {
        this.setActiveProjects(storedProjects, companyId, true);
      }
      return;
    }

    if (this.getProjectsInFlight) {
      return;
    }
    this.getProjectsInFlight = true;
    // UserPermissions (grantsReady) sonrası; yetki yoksa GetProjects çağırma
    this.permissionService.grantsReady
      .pipe(
        filter((ready) => ready),
        take(1),
        switchMap(() => {
          if (
            !this.permissionService.hasAnyRead(
              P.GorevPanosu.Module,
              P.GorevPanosu.ProjeYonetimi,
            )
          ) {
            return EMPTY;
          }
          return this.dashboardTaskService.getProjects();
        }),
        finalize(() => {
          this.getProjectsInFlight = false;
        }),
      )
      .subscribe({
        next: (list) => this.applyProjectsFromDashboard(list ?? []),
        error: () => {},
      });
  }

  refreshTasks(): void {
    if (!isActiveMasterMergeFeature('projectManagement')) {
      return;
    }

    this.activeProjectIds$.value.forEach((id) => this.loadTasks(id));
  }

  markTaskCompletedLocal(taskId: string): void {
    this.patchTaskStatusLocal(taskId, 'Completed');
  }

  patchTaskStatusLocal(taskId: string, status: string): void {
    this.tasksByProjectId.forEach((list, key) => {
      this.tasksByProjectId.set(
        key,
        (list || []).map((t) => (t.id === taskId ? { ...t, status } : t)),
      );
    });
    this.publishTasks();
    this.syncWorkflowTaskSessionFromTaskList(this.tasks$.value);
  }

  /**
   * GetProjectTasks / bellekteki görev güncellemesi sonrası: session’daki hesaplama görevi
   * (url görevleri hariç) güncel başlık, durum ve payload ile hizalanır.
   */
  private syncWorkflowTaskSessionFromTaskList(tasks: ProjectWorkflowTask[]): void {
    const raw = sessionStorage.getItem(PROJECT_WORKFLOW_TASK_CONTEXT_KEY);
    if (!raw) {
      return;
    }
    try {
      const ctx = JSON.parse(raw) as WorkflowTaskContext;
      if (ctx.contextKind === 'url') {
        return;
      }
      const taskId = ctx.taskId;
      if (!taskId) {
        return;
      }
      const fresh = tasks.find((t) => t.id === taskId);
      if (!fresh) {
        return;
      }
      const { categoryCode, methodCode } = extractCodesFromTask(fresh);
      const merged: WorkflowTaskContext = {
        ...ctx,
        status: fresh.status,
        title: fresh.title ?? ctx.title,
        actionPayloadJson: fresh.actionPayloadJson ?? ctx.actionPayloadJson,
        actionType: fresh.actionType ?? ctx.actionType,
        categoryCode: categoryCode ?? ctx.categoryCode ?? fresh.categoryCode,
        methodCode: methodCode ?? ctx.methodCode ?? fresh.methodCode,
      };
      sessionStorage.setItem(
        PROJECT_WORKFLOW_TASK_CONTEXT_KEY,
        JSON.stringify(merged),
      );
    } catch {
      /* ignore */
    }
  }

  /** Görevdeki dahili URL (http(s) değilse). */
  static resolveInternalTaskUrl(task: ProjectWorkflowTask): string | null {
    const tryNorm = (s: string | null | undefined): string | null => {
      const t = (s ?? '').trim();
      if (!t || /^https?:\/\//i.test(t) || t.startsWith('//')) {
        return null;
      }
      const path = t.startsWith('/') ? t : `/${t}`;
      return normalizeWorkflowPath(path);
    };

    const fromTop = tryNorm(task.url);
    if (fromTop) {
      return fromTop;
    }
    if (!task.actionPayloadJson) {
      return null;
    }
    try {
      const p = JSON.parse(task.actionPayloadJson) as { url?: string };
      return tryNorm(p?.url);
    } catch {
      return null;
    }
  }

  /**
   * Router URL’si ile eşleşen URL-temelli görev.
   * Önce Pending/InProgress; yoksa Completed da döner (durum geri alma / düzeltme).
   */
  findUrlTaskForRouterUrl(routerUrl: string): ProjectWorkflowTask | null {
    const tasks = this.tasks$.value;
    const matches: ProjectWorkflowTask[] = [];
    for (const t of tasks) {
      const p = ActiveProjectWorkflowService.resolveInternalTaskUrl(t);
      if (p && workflowPathsMatch(routerUrl, p)) {
        matches.push(t);
      }
    }
    if (matches.length === 0) {
      return null;
    }
    const open = matches.find((t) => !isTaskCompleted(t));
    return open ?? matches[0];
  }

  /**
   * Proje yönetiminden dahili URL’ye gidilirken — yüzen panel + oturum senkronu.
   */
  persistUrlTaskNavigationContext(
    task: ProjectWorkflowTask,
    internalRouterPath: string,
  ): void {
    const targetUrl = normalizeWorkflowPath(internalRouterPath);
    const ctx: WorkflowTaskContext = {
      contextKind: 'url',
      targetUrl,
      projectId: task.projectId,
      taskId: task.id,
      sourceQuestionId: task.sourceQuestionId,
      title: task.title,
      status: task.status,
      actionType: task.actionType ?? null,
      actionPayloadJson: task.actionPayloadJson ?? null,
      url: task.url ?? null,
      categoryCode: task.categoryCode ?? null,
      methodCode: task.methodCode ?? null,
    };
    sessionStorage.setItem(PROJECT_WORKFLOW_TASK_CONTEXT_KEY, JSON.stringify(ctx));
  }

  /**
   * methods/... routing katmanı: önce kategori düzeyi görevler; yoksa aynı kategorideki method görevleri.
   */
  pickTaskForRoutingLayer(url: string, route: ActivatedRoute): ProjectWorkflowTask | null {
    const params = collectRouteParams(route);
    const hint = resolveRouteHint(url, params);
    if (!hint.categoryCode) {
      return null;
    }
    const tasks = this.tasks$.value;
    const preferred = this.pickPreferredTaskFromSession(tasks, hint, {
      routingLayer: true,
    });
    if (preferred) {
      return preferred;
    }
    let best: ProjectWorkflowTask | null = null;
    let bestScore = -1;
    for (const t of tasks) {
      const s = scoreTaskMatch(t, hint, { routingLayer: true });
      if (s > bestScore) {
        bestScore = s;
        best = t;
      }
    }
    if (bestScore >= 0) {
      return best;
    }
    best = null;
    bestScore = -1;
    for (const t of tasks) {
      const s = scoreTaskMatch(t, hint);
      if (s > bestScore) {
        bestScore = s;
        best = t;
      }
    }
    return bestScore >= 0 ? best : null;
  }

  /**
   * Kart göstergesi: kategori ve isteğe bağlı method ile eşleşen aktif proje görevi.
   * categoryOnly=true → yalnızca kategori düzeyi görevler (hesaplama ana kartları).
   */
  findTaskForHint(
    hint: RouteTaskHint,
    options?: { categoryOnly?: boolean },
  ): ProjectWorkflowTask | null {
    if (!hint.categoryCode || !isActiveMasterMergeFeature('projectManagement')) {
      return null;
    }

    const tasks = this.tasks$.value;
    let best: ProjectWorkflowTask | null = null;
    let bestScore = -1;

    for (const t of tasks) {
      if (this.isProjectCompleted(t.projectId)) {
        continue;
      }
      const score = scoreTaskMatch(t, hint, {
        routingLayer: options?.categoryOnly ?? false,
      });
      if (score > bestScore) {
        bestScore = score;
        best = t;
      }
    }

    return bestScore >= 0 ? best : null;
  }

  pickTaskForRoute(url: string, route: ActivatedRoute): ProjectWorkflowTask | null {
    const path = url.split('?')[0].toLowerCase();
    if (path.includes('/methods/')) {
      return this.pickTaskForRoutingLayer(url, route);
    }
    const params = collectRouteParams(route);
    const hint = resolveRouteHint(url, params);
    if (!hint.categoryCode) {
      return null;
    }
    const tasks = this.tasks$.value;
    const preferred = this.pickPreferredTaskFromSession(tasks, hint);
    if (preferred) {
      return preferred;
    }
    let best: ProjectWorkflowTask | null = null;
    let bestScore = -1;
    for (const t of tasks) {
      const s = scoreTaskMatch(t, hint);
      if (s > bestScore) {
        bestScore = s;
        best = t;
      }
    }
    return bestScore >= 0 ? best : null;
  }

  private pickPreferredTaskFromSession(
    tasks: ProjectWorkflowTask[],
    hint: RouteTaskHint,
    options?: { routingLayer?: boolean },
  ): ProjectWorkflowTask | null {
    const raw = sessionStorage.getItem(PROJECT_WORKFLOW_TASK_CONTEXT_KEY);
    if (!raw) {
      return null;
    }
    try {
      const ctx = JSON.parse(raw) as WorkflowTaskContext;
      if (ctx.contextKind === 'url' || !ctx.taskId) {
        return null;
      }
      const candidate = tasks.find((t) => t.id === ctx.taskId);
      if (!candidate) {
        return null;
      }
      const score = scoreTaskMatch(candidate, hint, options);
      return score >= 0 ? candidate : null;
    } catch {
      return null;
    }
  }

  buildContextFromTask(
    task: ProjectWorkflowTask,
    route: ActivatedRoute,
  ): WorkflowTaskContext {
    const params = collectRouteParams(route);
    const { categoryCode, methodCode } = extractCodesFromTask(task);
    return {
      contextKind: 'calculation',
      projectId: task.projectId,
      taskId: task.id,
      title: task.title,
      status: task.status,
      methodCode: methodCode ?? undefined,
      categoryCode: categoryCode ?? undefined,
      categoryId: params['id'] ?? null,
      locationId: params['locationId'] ?? null,
      sourceQuestionId: task.sourceQuestionId,
      actionType: task.actionType ?? null,
      actionPayloadJson: task.actionPayloadJson ?? null,
      url: task.url ?? null,
    };
  }

  private setActiveProjects(
    projectIds: string[],
    companyId: string | null,
    reloadTasks: boolean,
  ): void {
    const ids = [...new Set((projectIds || []).filter(Boolean))];
    sessionStorage.setItem(SESSION_ACTIVE_PROJECT_IDS, JSON.stringify(ids));
    sessionStorage.setItem(SESSION_ACTIVE_PROJECT_ID, ids[0] ?? '');
    if (companyId) {
      sessionStorage.setItem(SESSION_ACTIVE_COMPANY_ID, companyId);
    }
    const prevIds = this.activeProjectIds$.value;
    this.activeProjectIds$.next(ids);
    this.activeProjectId$.next(ids[0] ?? null);
    const removed = prevIds.filter((id) => !ids.includes(id));
    removed.forEach((id) => {
      this.tasksSubs.get(id)?.unsubscribe();
      this.tasksSubs.delete(id);
      this.loadTasksProjectIds.delete(id);
      this.tasksByProjectId.delete(id);
    });
    ids.forEach((id) => {
      if (reloadTasks || !this.tasksByProjectId.has(id)) {
        this.loadTasks(id);
      }
    });
    if (removed.length) {
      this.publishTasks();
    }
    this.writeCrossTabSync(ids, companyId);
  }

  private loadTasks(projectId: string): void {
    if (!isActiveMasterMergeFeature('projectManagement')) {
      return;
    }

    if (this.loadTasksProjectIds.has(projectId)) {
      return;
    }
    this.loadTasksProjectIds.add(projectId);
    this.tasksSubs.get(projectId)?.unsubscribe();
    const sub = this.projectManagementService
      .getProjectTasks(projectId)
      .pipe(
        finalize(() => {
          this.tasksSubs.delete(projectId);
          this.loadTasksProjectIds.delete(projectId);
        }),
      )
      .subscribe({
        next: (tasks) => {
          const list = tasks ?? [];
          this.tasksByProjectId.set(projectId, list);
          this.publishTasks();
          this.syncWorkflowTaskSessionFromTaskList(this.tasks$.value);
        },
        error: () => {
          this.tasksByProjectId.set(projectId, []);
          this.publishTasks();
        },
      });
    this.tasksSubs.set(projectId, sub);
  }

  isProjectCompleted(projectId: string | null | undefined): boolean {
    const id = String(projectId ?? '').trim();
    if (!id) {
      return false;
    }
    const map = this.readProjectStatuses();
    const status = map[id];
    const n = normalizeStatus(status);
    return n === 'completed' || n === 'complete';
  }

  private persistProjectStatuses(projects: DashboardProject[]): void {
    const out: Record<string, string> = {};
    for (const p of projects || []) {
      const id = String(p?.projectId ?? '').trim();
      if (!id) {
        continue;
      }
      out[id] = String(p?.status ?? '');
    }
    sessionStorage.setItem(SESSION_PROJECT_STATUS_BY_ID, JSON.stringify(out));
  }

  private readProjectStatuses(): Record<string, string> {
    const raw = sessionStorage.getItem(SESSION_PROJECT_STATUS_BY_ID);
    if (!raw) {
      return {};
    }
    try {
      const parsed = JSON.parse(raw) as Record<string, string>;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private publishTasks(): void {
    const ids = this.activeProjectIds$.value;
    const merged: ProjectWorkflowTask[] = [];
    for (const id of ids) {
      const list = this.tasksByProjectId.get(id) ?? [];
      merged.push(...list);
    }
    this.tasks$.next(merged);
  }

  private readPersistedProjectIds(): string[] {
    const json = sessionStorage.getItem(SESSION_ACTIVE_PROJECT_IDS);
    if (json) {
      try {
        const list = JSON.parse(json) as string[];
        if (Array.isArray(list)) {
          return [...new Set(list.filter(Boolean))];
        }
      } catch {
        /* ignore */
      }
    }
    const single = sessionStorage.getItem(SESSION_ACTIVE_PROJECT_ID);
    return single ? [single] : [];
  }

  private readSyncedProjectIds(): string[] {
    const json = localStorage.getItem(LS_SYNC_PROJECT_IDS);
    if (json) {
      try {
        const list = JSON.parse(json) as string[];
        if (Array.isArray(list)) {
          return [...new Set(list.filter(Boolean))];
        }
      } catch {
        /* ignore */
      }
    }
    const single = localStorage.getItem(LS_SYNC_PROJECT_ID);
    return single ? [single] : [];
  }
}
